/**
 * Design Tokens / Theme Configuration
 *
 * Centralized design system for colors, spacing, typography, and breakpoints.
 * These tokens are used throughout the app and mapped to CSS variables for runtime theming.
 */

// =============================================================================
// BREAKPOINTS
// =============================================================================
export const breakpoints = {
  xs: 0,
  sm: 480,   // Mobile landscape
  md: 768,   // Tablet
  lg: 1024,  // Desktop
  xl: 1280,  // Large desktop
};

// Media query helpers (for use in JS if needed)
export const mediaQueries = {
  sm: `@media (min-width: ${breakpoints.sm}px)`,
  md: `@media (min-width: ${breakpoints.md}px)`,
  lg: `@media (min-width: ${breakpoints.lg}px)`,
  xl: `@media (min-width: ${breakpoints.xl}px)`,
};

// =============================================================================
// SPACING
// =============================================================================
export const spacing = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
};

// =============================================================================
// TYPOGRAPHY
// =============================================================================
export const typography = {
  fontFamily: {
    serif: "'Merriweather', Georgia, serif",
    sans: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  fontSize: {
    xs: '11px',
    sm: '12px',
    base: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '29px',
    '4xl': '36px',
  },
  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    black: '900',
  },
  lineHeight: {
    tight: '1.1',
    snug: '1.25',
    normal: '1.5',
    relaxed: '1.625',
  },
};

// =============================================================================
// COLORS - Light Theme (Default)
// =============================================================================
export const colorsLight = {
  // Background colors
  background: {
    primary: '#f8f7f4',      // Main page background (warm off-white)
    secondary: '#ffffff',    // Card background
    tertiary: '#f5f5f5',     // Header/section backgrounds
    elevated: '#ffffff',     // Elevated surfaces (tiles, cards)
    muted: '#f3f4f6',        // Muted backgrounds
    overlay: 'rgba(0, 0, 0, 0.5)', // Modal overlays
  },

  // Text colors
  text: {
    primary: '#1F2937',      // Primary text
    secondary: '#3F3F46',    // Headings
    tertiary: '#52525B',     // Secondary text
    muted: '#71717A',        // Muted/helper text
    disabled: '#9CA3AF',     // Disabled text
    inverse: '#FFFFFF',      // Text on dark backgrounds
  },

  // Border colors
  border: {
    light: '#F1F5F9',
    default: '#E2E8F0',
    medium: '#E5E7EB',
    strong: '#CBD5E1',
  },

  // Brand colors
  brand: {
    primary: '#4B9B8C',      // Teal - main brand color
    primaryHover: '#3D8274',
    secondary: '#60A5FA',    // Blue - accent
    secondaryHover: '#3B82F6',
  },

  // Semantic / Status colors
  status: {
    success: '#22C55E',
    successLight: 'rgba(220, 252, 231, 0.9)',
    successText: '#166534',

    warning: '#EAB308',
    warningLight: 'rgba(254, 249, 195, 0.9)',
    warningText: '#854D0E',

    error: '#EF4444',
    errorLight: 'rgba(254, 226, 226, 0.9)',
    errorText: '#991B1B',

    info: '#228B22',
    infoLight: 'rgba(220, 252, 231, 0.9)',
    infoText: '#166534',
  },

  // Score colors (for ski quality ratings)
  score: {
    excellent: '#22C55E',
    good: '#EAB308',
    fair: '#94A3B8',
    poor: '#EF4444',
  },

  // Weather-specific colors
  weather: {
    sunny: '#F59E0B',
    cloudy: '#94A3B8',
    rain: '#64748B',
    snow: '#3B82F6',
    night: '#1E293B',
  },

  // Interactive colors
  interactive: {
    hover: 'rgba(248, 250, 252, 0.8)',
    active: 'rgba(0, 0, 0, 0.04)',
    focus: '#3B82F6',
  },

  // Chart/graph colors
  chart: {
    blue: '#3B82F6',
    blueLight: 'rgba(96, 165, 250, 0.55)',
    track: 'rgba(180, 180, 180, 0.5)',
    marker: '#1F2937',
  },

  // Debug colors
  debug: {
    purple: '#A855F7',
  },
};

// =============================================================================
// COLORS - Dark Theme
// =============================================================================
export const colorsDark = {
  // Background colors
  background: {
    primary: '#0F172A',      // Main page background
    secondary: '#1E293B',    // Card background
    tertiary: '#334155',     // Header/section backgrounds
    elevated: '#1E293B',     // Elevated surfaces
    muted: '#334155',        // Muted backgrounds
    overlay: 'rgba(0, 0, 0, 0.7)', // Modal overlays
  },

  // Text colors
  text: {
    primary: '#F8FAFC',      // Primary text
    secondary: '#E2E8F0',    // Headings
    tertiary: '#CBD5E1',     // Secondary text
    muted: '#94A3B8',        // Muted/helper text
    disabled: '#64748B',     // Disabled text
    inverse: '#0F172A',      // Text on light backgrounds
  },

  // Border colors
  border: {
    light: '#ffffff14',
    default: '#ffffff21',
    medium: '#ffffff21',
    strong: '#ffffff30',
  },

  // Brand colors (same in dark mode, slightly adjusted)
  brand: {
    primary: '#5EEAD4',      // Teal - lighter for dark mode
    primaryHover: '#2DD4BF',
    secondary: '#60A5FA',    // Blue - accent
    secondaryHover: '#93C5FD',
  },

  // Semantic / Status colors (adjusted for dark mode)
  status: {
    success: '#4ADE80',
    successLight: 'rgba(22, 101, 52, 0.4)',
    successText: '#86EFAC',

    warning: '#FACC15',
    warningLight: 'rgba(133, 77, 14, 0.4)',
    warningText: '#FDE047',

    error: '#F87171',
    errorLight: 'rgba(153, 27, 27, 0.4)',
    errorText: '#FCA5A5',

    info: '#4ADE80',
    infoLight: 'rgba(22, 101, 52, 0.4)',
    infoText: '#86EFAC',
  },

  // Score colors
  score: {
    excellent: '#4ADE80',
    good: '#FACC15',
    fair: '#94A3B8',
    poor: '#F87171',
  },

  // Weather-specific colors
  weather: {
    sunny: '#FBBF24',
    cloudy: '#94A3B8',
    rain: '#94A3B8',
    snow: '#60A5FA',
    night: '#1E293B',
  },

  // Interactive colors
  interactive: {
    hover: 'rgba(51, 65, 85, 0.5)',
    active: 'rgba(255, 255, 255, 0.08)',
    focus: '#60A5FA',
  },

  // Chart/graph colors
  chart: {
    blue: '#60A5FA',
    blueLight: 'rgba(96, 165, 250, 0.4)',
    track: 'rgba(100, 116, 139, 0.5)',
    marker: '#E2E8F0',
  },

  // Debug colors
  debug: {
    purple: '#C084FC',
  },
};

// =============================================================================
// SHADOWS
// =============================================================================
export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  default: '0 2px 8px rgba(0, 0, 0, 0.08)',
  md: '0 4px 12px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 25px rgba(0, 0, 0, 0.15)',
  xl: '0 20px 50px rgba(0, 0, 0, 0.2)',
};

export const shadowsDark = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
  default: '0 2px 8px rgba(0, 0, 0, 0.4)',
  md: '0 4px 12px rgba(0, 0, 0, 0.5)',
  lg: '0 10px 25px rgba(0, 0, 0, 0.6)',
  xl: '0 20px 50px rgba(0, 0, 0, 0.7)',
};

// =============================================================================
// BORDER RADIUS
// =============================================================================
export const radii = {
  none: '0',
  sm: '4px',
  default: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  full: '9999px',
};

// =============================================================================
// TRANSITIONS
// =============================================================================
export const transitions = {
  fast: '0.15s ease',
  default: '0.2s ease',
  slow: '0.3s ease',
};

// =============================================================================
// Z-INDEX
// =============================================================================
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  modal: 300,
  tooltip: 400,
  toast: 500,
};

// =============================================================================
// TOUCH TARGETS (for mobile accessibility)
// =============================================================================
export const touchTargets = {
  min: '44px',  // Minimum touch target size (Apple HIG recommendation)
  comfortable: '48px',
};

// =============================================================================
// THEME EXPORT
// =============================================================================
export const themes = {
  light: {
    colors: colorsLight,
    shadows,
  },
  dark: {
    colors: colorsDark,
    shadows: shadowsDark,
  },
};

export default {
  breakpoints,
  mediaQueries,
  spacing,
  typography,
  themes,
  radii,
  transitions,
  zIndex,
  touchTargets,
};
